import { Box, useColorScheme } from "@mui/material";
import Lottie from "lottie-react";
import darkHeroAnimation from "./assets/dark_mode/optimized_hero.json";
import lightHeroAnimation from "./assets/light_mode/optimized_hero.json";

const W = 653;
const H = 612;
const px = (v: number) => `${(v / W) * 100}%`;
const py = (v: number) => `${(v / H) * 100}%`;
const pw = (v: number) => `${(v / W) * 100}%`;
const ph = (v: number) => `${(v / H) * 100}%`;

// New face — replaces the face on the robot's head
const HeroFace: React.FC = () => (
  <svg width="100%" height="100%" viewBox="0 0 177 184" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="hf_inner" x="0.271484" y="0.354004" width="176.566" height="183.867" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset dy="2.15448"/>
        <feGaussianBlur stdDeviation="12.9269"/>
        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
        <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.37 0"/>
        <feBlend mode="normal" in2="shape" result="effect1_innerShadow"/>
      </filter>
      <linearGradient id="hf_grad" x1="58.2188" y1="70.0331" x2="174.297" y2="90.9943" gradientUnits="userSpaceOnUse">
        <stop stopColor="#2542FB"/>
        <stop offset="1" stopColor="#596BDF"/>
      </linearGradient>
    </defs>
    <g opacity="1" filter="url(#hf_inner)">
      <path d="M1.2403 45.1596L0.271853 152.841C0.12314 169.376 13.6852 182.753 30.2058 182.039C65.338 180.521 103.822 177.943 139.261 175.894C159.232 174.739 175.059 158.441 175.404 138.439L176.837 55.3935L176.233 31.3176C175.94 19.6532 169.939 8.87605 160.178 2.48445C157.72 0.875423 154.792 0.143225 151.866 0.406473L32.6212 11.1366C14.9703 12.7249 1.39968 27.4381 1.2403 45.1596Z" fill="url(#hf_grad)"/>
    </g>
    {/* Mascot displayed on the head screen (replaces the old "oo" smiley face) */}
    <svg x={13} y={18} width={152} height={152} viewBox="77 62 110 110">
      <path d="M100.668 151.591C93.9693 146.993 91.484 138.389 90.5891 133.3C88.7667 122.936 86.8919 118.31 91.1081 106.9C99.169 85.0846 126.878 75.8721 148.348 79.4359C154.45 80.4488 158.637 89.7963 163.538 91.4063C181.346 97.2564 171.281 111.889 174.298 125.996C174.255 137.983 159.452 153.309 152.957 154.698C141.059 157.243 136.686 155.474 122.392 153.862C113.916 152.905 107.73 156.438 100.668 151.591Z" fill="#3DA7E7"/>
      <path d="M110.624 141.387C106.014 138.222 104.303 132.3 103.687 128.797C102.433 121.663 101.142 118.479 104.044 110.625C109.593 95.6096 128.665 89.2685 143.443 91.7216C147.643 92.4188 150.525 98.8528 153.899 99.961C166.156 103.988 159.228 114.06 161.305 123.77C161.276 132.021 151.086 142.569 146.615 143.526C138.426 145.277 135.416 144.06 125.578 142.95C119.743 142.292 115.485 144.723 110.624 141.387Z" fill="#4ABBFF"/>
      <circle cx="3.57831" cy="3.57831" r="3.57831" transform="matrix(-1 0 0 1 148.238 111.003)" fill="#2C2727"/>
      <circle cx="3.57831" cy="3.57831" r="3.57831" transform="matrix(-1 0 0 1 123.905 111.003)" fill="#2C2727"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M136.071 121.022C136.071 122.899 134.256 124.601 131.778 124.601C129.299 124.601 127.484 122.899 127.484 121.022H126.052C126.052 123.889 128.723 126.032 131.778 126.032C134.832 126.032 137.503 123.889 137.503 121.022H136.071Z" fill="black"/>
    </svg>
  </svg>
);

const HeroVisual: React.FC = () => {
  const { mode, systemMode } = useColorScheme();
  const isLight = mode === "light" || (mode === "system" && systemMode === "light");

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        paddingTop: `${(H / W) * 100}%`,
        display: { xs: "none", md: "block" },
      }}
    >
      <Box sx={{ position: "absolute", inset: 0, overflow: "visible" }}>

        {/* Lottie animation — full scene (robot body, badges, bubbles) */}
        <Box sx={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }}>
          <Lottie
            animationData={isLight ? lightHeroAnimation : darkHeroAnimation}
            loop={true}
            style={{ width: "100%", height: "100%" }}
          />
        </Box>

        {/* New face SVG — covers only the robot's head face area */}
        <Box sx={{
          position: "absolute",
          left: px(210),
          top: py(40),
          width: pw(138),
          height: ph(144),
          zIndex: 2,
          pointerEvents: "none",
        }}>
          <HeroFace />
        </Box>

      </Box>
    </Box>
  );
};

export default HeroVisual;
